/*
 * companies.ts — Company loading and selection dropdown management.
 *
 * Two modes, switched on USE_TASKPANE_CONFIGURATION:
 *  - Taskpane mode (true): the BC API endpoint is entered manually in Settings.
 *    Companies are loaded from that single endpoint (unchanged, original behavior).
 *  - Runtime mode (false): the taskpane endpoint field is hidden; endpoints instead
 *    come from server/env config (BC_API_ENDPOINTS). Once signed in, companies are
 *    fetched from every configured endpoint in parallel and merged into one dropdown.
 *    Selecting a company also pins the BC API endpoint it came from — that endpoint is
 *    what every subsequent BC call (customers, orders, attachments) uses.
 */

/* global document, localStorage, fetch */

import { parseODataError, escapeODataLiteral, isCredentialsRejectedError, buildODataQuery } from "../shared/odata";
import {
  STORAGE_KEY_BC_URL,
  STORAGE_KEY_COMPANY_ID,
  STORAGE_KEY_COMPANY_NM,
  STORAGE_KEY_COMPANY_ACRONYM,
  STORAGE_KEY_DEPARTMENT_SET_ENTRY_ID_FLT,
} from "../shared/settings";
import type { BcApiEndpointDescriptor } from "../shared/runtime-config";
import {
  getUseTaskpaneConfiguration,
  getRuntimeBcApiEndpoints,
  getAllowedBcApiHosts,
  getRuntimeAllowInsecureBcEndpoint,
  getCurrentAccount,
} from "../shared/auth-state";
import { validateBcApiEndpoint } from "../shared/bc-endpoint";
import { getBcConnectionContext, loadSettings, ensureAccessToken } from "./taskpane-auth";
import { showCompanySection } from "./helpers";


// ─── Company change callback ────────────────

export type CompanyChangedCallback = () => void | Promise<void>;
let onCompanyChanged: CompanyChangedCallback | null = null;
let companySelectListenerWired = false;

export function setOnCompanyChanged(callback: CompanyChangedCallback): void {
  onCompanyChanged = callback;
}

interface AggregatedCompany {
  id: string;
  name: string;
  // BC API endpoint this company was loaded from — carried on the option's dataset and
  // pinned to storage when the company is selected, so every later BC call uses it.
  bcUrl: string;
}

interface ODataListResponse<T> {
  value?: T[];
}

// BC userSecurityId values are GUIDs; validate before interpolating into an OData filter.
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatCompaniesHttpError(status: number, responseText: string, requestUrl: string): string {
  const odataError = parseODataError(responseText);
  if (odataError) return `BC API error: ${odataError}`;

  if (status === 401) return `Unauthorized (401). Sign in again and verify API permissions. URL: ${requestUrl}`;
  if (status === 403) return `Forbidden (403). Your account/app cannot read companies. URL: ${requestUrl}`;
  if (status === 404) return `Endpoint not found (404). Verify BC API endpoint path/version. URL: ${requestUrl}`;
  if (status >= 500) return `Business Central server error (HTTP ${status}). Try again later. URL: ${requestUrl}`;
  return `BC request failed (HTTP ${status}). URL: ${requestUrl}`;
}

function formatCompaniesNetworkError(err: unknown, requestUrl: string): string {
  const raw = err instanceof Error ? err.message : String(err ?? "Request failed");
  if (err instanceof DOMException && err.name === "AbortError") {
    return "Company request was cancelled.";
  }

  if (/failed to fetch|networkerror/i.test(raw)) {
    return `Cannot load companies. Check your network connection and verify the BC API endpoint (instance/version path). Current URL: ${requestUrl}`;
  }

  return `Request failed: ${raw}. URL: ${requestUrl}`;
}

// ─── Status UI ───────────────────────────────

/**
 * The company-load-status element lives inside the settings panel, so its (often detailed,
 * URL-bearing) message is invisible unless the user opens Settings first. Any error here
 * also flips on a short, generic banner on the main page -- see setCompanyLoadBanner --
 * mirroring the "Not authenticated" auth-banner pattern so a failure like the VPN being
 * disconnected is noticeable without hunting for it.
 */
function setCompanyLoadStatus(message: string, isError = false): void {
  const statusEl = document.getElementById("company-load-status");
  if (statusEl) {
    statusEl.textContent = message;
    statusEl.classList.toggle("company-load-status--error", isError && !!message);
  }
  setCompanyLoadBanner(isError && !!message);
}

function setCompanyLoadBanner(show: boolean): void {
  const el = document.getElementById("company-load-banner");
  if (el) el.style.display = show ? "flex" : "none";
}

/** Hides both the detailed settings-panel status and the main-page banner. */
export function clearCompanyLoadStatus(): void {
  setCompanyLoadStatus("");
}

function formatFailureSummary(failures: { endpointLabel: string; error: string }[]): string {
  if (failures.length === 0) return "";
  const [first, ...rest] = failures;
  const more = rest.length > 0 ? ` (+${rest.length} more endpoint${rest.length === 1 ? "" : "s"} failed)` : "";
  return `${first.endpointLabel}: ${first.error}${more}`;
}

async function fetchBcOData<T>(url: string, token: string): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(url, { headers: { Authorization: `Bearer ${token}`, Accept: "application/json" } });
  } catch (err: unknown) {
    throw new Error(formatCompaniesNetworkError(err, url));
  }
  if (!resp.ok) {
    throw new Error(formatCompaniesHttpError(resp.status, await resp.text(), url));
  }
  return (await resp.json()) as T;
}

// ─── Per-user company access (multi-endpoint mode only) ─────

interface UserDetailsLookup {
  userSecurityId: string | null;
  departmentSetEntryIdFlt: string;
}

/**
 * Looks up the signed-in user's user_details row for this company by email -- one request
 * serving both callers that need it: filterCompaniesForCurrentUser (multi-endpoint
 * permission filtering, needs userSecurityId) and loadAndStoreDepartmentSetEntryId (needs
 * departmentSetEntryIdFlt, runs on every company selection in both modes). user_details is
 * company-wide data, but BC's OData routes still require a companies(id) path segment —
 * any company in the database works, so the caller passes one from the just-fetched company
 * list (or the just-selected one). userSecurityId comes back null if no matching row
 * exists — the account simply isn't provisioned in this particular BC database, a normal,
 * silent outcome; departmentSetEntryIdFlt comes back "" in that same case.
 */
async function fetchUserDetails(
  bcUrl: string,
  companyId: string,
  token: string,
  email: string,
): Promise<UserDetailsLookup> {
  const query = buildODataQuery({
    $filter: `authenticationEmail eq '${escapeODataLiteral(email).toLowerCase()}'`,
  });
  const url = `${bcUrl}/api/logiqstar/automation/v2.0/companies(${companyId})/user_details${query}`;
  const data = await fetchBcOData<ODataListResponse<{ userSecurityId?: string; departmentSetEntryIdFlt?: string }>>(url, token);
  const row = (data.value ?? [])[0];
  const securityId = row?.userSecurityId?.trim() ?? "";
  return {
    userSecurityId: GUID_RE.test(securityId) ? securityId : null,
    departmentSetEntryIdFlt: row?.departmentSetEntryIdFlt?.trim() ?? "",
  };
}

/**
 * Resolves which companies the given userSecurityId may access in this database:
 *  - "ALL" if a blank-company (all-companies) permission set is assigned, checked first --
 *    a user can hold both a blank-company set and company-specific ones at once (seen on
 *    APAC), and the blank one should win outright rather than being shadowed by the
 *    specific ones
 *  - otherwise, a specific list of company names, if per-company permission sets are assigned
 *  - [] if neither — the user has no access to any company at this endpoint
 * userPermissions is company-wide too; see fetchUserSecurityId for why companyId is required.
 */
async function fetchPermittedCompanyNames(
  bcUrl: string,
  companyId: string,
  token: string,
  userSecurityId: string,
): Promise<string[] | "ALL"> {
  const base = `${bcUrl}/api/microsoft/automation/v2.0/companies(${companyId})/userPermissions`;
  const blanket = await fetchBcOData<ODataListResponse<{ company?: string }>>(
    `${base}${buildODataQuery({ $filter: `company eq '' and userSecurityId eq ${userSecurityId}` })}`,
    token,
  );
  if ((blanket.value ?? []).length > 0) return "ALL";

  const scoped = await fetchBcOData<ODataListResponse<{ company?: string }>>(
    `${base}${buildODataQuery({ $filter: `company ne '' and userSecurityId eq ${userSecurityId}` })}`,
    token,
  );
  const companyNames = Array.from(
    new Set((scoped.value ?? []).map((p) => (p.company ?? "").trim()).filter((name) => name.length > 0)),
  );
  return companyNames.length > 0 ? companyNames : [];
}

/**
 * Filters rawCompanies down to the ones the signed-in user can access at this endpoint,
 * per BC's userPermissions. A genuine HTTP/network failure here propagates (caller
 * treats the endpoint as failed); "no access" outcomes resolve to [] silently.
 */
async function filterCompaniesForCurrentUser(
  bcUrl: string,
  token: string,
  rawCompanies: { id: string; name: string }[],
): Promise<{ id: string; name: string }[]> {
  if (rawCompanies.length === 0) return [];

  const email = getCurrentAccount()?.username ?? "";
  if (!email) throw new Error("Not signed in.");

  // user_details/userPermissions are company-wide; any company id routes to the same data.
  const companyId = rawCompanies[0].id;

  const { userSecurityId } = await fetchUserDetails(bcUrl, companyId, token, email);
  if (!userSecurityId) return [];

  const permitted = await fetchPermittedCompanyNames(bcUrl, companyId, token, userSecurityId);
  if (permitted === "ALL") return rawCompanies;

  const permittedLower = new Set(permitted.map((name) => name.toLowerCase()));
  return rawCompanies.filter((c) => permittedLower.has((c.name ?? "").trim().toLowerCase()));
}

// ─── Load companies ─────────────────────────

export async function loadCompanies(): Promise<void> {
  if (getUseTaskpaneConfiguration()) {
    await loadCompaniesSingleEndpoint();
  } else {
    await loadCompaniesMultiEndpoint();
  }
}

async function loadCompaniesSingleEndpoint(): Promise<void> {
  let bcUrl = "";
  let token = "";
  try {
    const connection = await getBcConnectionContext(false);
    bcUrl = connection.bcUrl;
    token = connection.token;
  } catch (err: any) {
    setCompanyLoadStatus(err?.message ?? "Unable to load companies.", true);
    return;
  }

  setCompanyLoadStatus("Loading companies...");

  showCompanySection(true);

  const companiesUrl = `${bcUrl.replace(/\/$/, "")}/api/logiqstar/ffw/v2.0/companies`;
  try {
    const resp = await fetch(companiesUrl, {
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });

    if (!resp.ok) {
      const msg = formatCompaniesHttpError(resp.status, await resp.text(), companiesUrl);
      setCompanyLoadStatus(`Error: ${msg}`, true);
      return;
    }

    const data = await resp.json();
    const rawCompanies: { id: string; name: string }[] = data.value ?? [];
    const companies: AggregatedCompany[] = rawCompanies.map((c) => ({ ...c, bcUrl }));

    populateCompanySelect(companies);
    setCompanyLoadStatus(companies.length === 0 ? "No companies found." : "");
  } catch (err: unknown) {
    setCompanyLoadStatus(`Error: ${formatCompaniesNetworkError(err, companiesUrl)}`, true);
  }
}

// ─── Multi-endpoint aggregation (runtime mode) ──────

type EndpointFetchResult =
  | { ok: true; companies: AggregatedCompany[] }
  | { ok: false; endpointLabel: string; error: string };

async function fetchCompaniesFromEndpoint(
  endpoint: BcApiEndpointDescriptor,
  token: string,
  allowedHosts: string[],
  allowInsecureHttp: boolean,
): Promise<EndpointFetchResult> {
  const label = endpoint.name || endpoint.url;
  const validation = validateBcApiEndpoint(endpoint.url, { allowedHosts, allowInsecureHttp });
  if (!validation.ok) {
    return { ok: false, endpointLabel: label, error: validation.error };
  }

  const bcUrl = validation.normalizedUrl;
  const companiesUrl = `${bcUrl}/api/logiqstar/ffw/v2.0/companies`;
  try {
    const data = await fetchBcOData<ODataListResponse<{ id: string; name: string }>>(companiesUrl, token);
    const permitted = await filterCompaniesForCurrentUser(bcUrl, token, data.value ?? []);
    const companies: AggregatedCompany[] = permitted.map((c) => ({ ...c, bcUrl }));
    return { ok: true, companies };
  } catch (err: unknown) {
    const error = err instanceof Error ? err.message : formatCompaniesNetworkError(err, companiesUrl);
    // "Not provisioned in this environment" is a normal, per-endpoint outcome (see
    // isCredentialsRejectedError) -- resolve to zero companies silently instead of
    // reporting an endpoint failure, same as an empty userPermissions result would.
    if (isCredentialsRejectedError(error)) {
      return { ok: true, companies: [] };
    }
    return { ok: false, endpointLabel: label, error };
  }
}

async function loadCompaniesMultiEndpoint(): Promise<void> {
  const token = await ensureAccessToken(false);
  if (!token) {
    setCompanyLoadStatus("Not signed in.", true);
    return;
  }

  const endpoints = getRuntimeBcApiEndpoints();
  if (endpoints.length === 0) {
    setCompanyLoadStatus("No BC API endpoints configured.", true);
    return;
  }

  setCompanyLoadStatus(`Loading companies from ${endpoints.length} endpoint(s)...`);
  showCompanySection(true);

  const allowedHosts = getAllowedBcApiHosts();
  const allowInsecureHttp = getRuntimeAllowInsecureBcEndpoint();

  const results = await Promise.all(
    endpoints.map((endpoint) => fetchCompaniesFromEndpoint(endpoint, token, allowedHosts, allowInsecureHttp)),
  );

  const companies: AggregatedCompany[] = [];
  const failures: { endpointLabel: string; error: string }[] = [];

  for (const result of results) {
    if (result.ok) {
      companies.push(...result.companies);
    } else {
      failures.push({ endpointLabel: result.endpointLabel, error: result.error });
    }
  }

  companies.sort((a, b) => a.name.localeCompare(b.name));
  populateCompanySelect(companies);

  if (companies.length === 0 && failures.length > 0) {
    setCompanyLoadStatus(
      `Unable to load companies from any endpoint. ${formatFailureSummary(failures)}`,
      true,
    );
  } else if (companies.length === 0) {
    setCompanyLoadStatus("No companies found.");
  } else if (failures.length > 0) {
    const companyWord = companies.length === 1 ? "company" : "companies";
    setCompanyLoadStatus(
      `Loaded ${companies.length} ${companyWord}. Some endpoints failed: ${formatFailureSummary(failures)}`,
      true,
    );
  } else {
    setCompanyLoadStatus("");
  }
}

// ─── Company acronym (lqs_companies) ────────

/**
 * Looks up the companyAcronym for the given company from its lqs_companies sub-resource
 * (expected to contain exactly one record) and saves it to storage. Non-fatal on failure —
 * the acronym is an auxiliary value used later; company selection itself already succeeded.
 */
async function loadAndStoreCompanyAcronym(bcUrl: string, companyId: string): Promise<void> {
  try {
    const token = await ensureAccessToken(false);
    if (!token) return;

    const url = `${bcUrl}/api/logiqstar/ffw/v2.0/companies(${companyId})/lqs_companies`;
    const data = await fetchBcOData<ODataListResponse<{ companyAcronym?: string }>>(url, token);
    const acronym = (data.value ?? [])[0]?.companyAcronym?.trim() ?? "";
    if (acronym) {
      localStorage.setItem(STORAGE_KEY_COMPANY_ACRONYM, acronym);
    } else {
      localStorage.removeItem(STORAGE_KEY_COMPANY_ACRONYM);
    }
  } catch (err: unknown) {
    console.error("Failed to load company acronym:", err);
  }
}

// ─── Department set entry ID (user_details) ─

// True from the moment a department fetch starts until it settles (success, failure, or
// short-circuit). departmentSetEntryIdFlt in storage is blank both while this is true (we
// haven't asked yet, or are mid-ask) and once it's false with a genuinely empty result --
// isDepartmentLoadPending is what lets orders.ts's "no department" banner (see
// updateDepartmentStatusBanner) tell those two states apart, so it only shows once we
// actually know, instead of on every transient clear-before-refetch.
let departmentLoadPending = false;

export function isDepartmentLoadPending(): boolean {
  return departmentLoadPending;
}

/**
 * Looks up the signed-in user's departmentSetEntryIdFlt for the given company (via the
 * shared fetchUserDetails -- same underlying user_details lookup filterCompaniesForCurrentUser
 * already uses for userSecurityId) and saves it to storage. Runs on every company selection
 * regardless of mode, unlike that multi-endpoint-only caller. Every email_bridge_orders/
 * operationalOrders request filters on this value -- see bc-api.ts. Non-fatal on failure,
 * matching loadAndStoreCompanyAcronym: it's an auxiliary value fetched after company
 * selection has already succeeded, not a precondition for it. A failed fetch deliberately
 * leaves storage blank (fail closed, same as a confirmed-empty result) rather than restoring
 * the previous company's value -- isDepartmentLoadPending is what keeps the banner honest
 * about that gap without needing to fake a stale value here.
 */
async function loadAndStoreDepartmentSetEntryId(bcUrl: string, companyId: string): Promise<void> {
  departmentLoadPending = true;
  try {
    const token = await ensureAccessToken(false);
    if (!token) return;

    const email = getCurrentAccount()?.username ?? "";
    if (!email) return;

    const { departmentSetEntryIdFlt } = await fetchUserDetails(bcUrl, companyId, token, email);
    if (departmentSetEntryIdFlt) {
      localStorage.setItem(STORAGE_KEY_DEPARTMENT_SET_ENTRY_ID_FLT, departmentSetEntryIdFlt);
    } else {
      localStorage.removeItem(STORAGE_KEY_DEPARTMENT_SET_ENTRY_ID_FLT);
    }
  } catch (err: unknown) {
    console.error("Failed to load department set entry ID:", err);
  } finally {
    departmentLoadPending = false;
  }
}

// ─── Dropdown population (shared by both modes) ─────

function populateCompanySelect(companies: AggregatedCompany[]): void {
  const sel = document.getElementById("select-company") as HTMLSelectElement;
  if (!sel) return;

  const savedId = loadSettings().companyId;

  // Rebuild options
  sel.innerHTML = `<option value="">— select a company —</option>`;
  let matchedSavedCompany: AggregatedCompany | null = null;
  companies.forEach((c) => {
    const opt = document.createElement("option");
    opt.value = c.id;
    opt.textContent = c.name;
    opt.dataset.bcUrl = c.bcUrl;
    if (c.id === savedId) {
      opt.selected = true;
      matchedSavedCompany = c;
    }
    sel.appendChild(opt);
  });

  // opt.selected = true above does NOT fire the "change" listener below, so re-matching the
  // previously-selected company from a fresh fetch (e.g. after sign-in) never went through
  // the bcUrl-pinning logic that a manual re-selection does. If the stored bcUrl had drifted
  // from what this company actually resolves to now (endpoint config changed, a stale/wrong
  // value from a previous session, etc.), every BC call kept silently using the wrong host
  // until the user manually re-picked the company -- previously reported as a 404 after
  // reopening the task pane, "fixed" by reselecting a different company and back. Re-syncing
  // storage here whenever the current fetch disagrees makes that self-healing automatic.
  if (matchedSavedCompany) {
    resyncSelectedCompanyStorage(matchedSavedCompany);
  }

  // Wire change handler only once
  if (!companySelectListenerWired) {
    companySelectListenerWired = true;
    sel.addEventListener("change", () => {
      const selectedOption = sel.options[sel.selectedIndex];
      const selectedId = sel.value;
      const selectedName = selectedOption?.text ?? "";
      const bcUrl = selectedOption?.dataset.bcUrl ?? "";
      localStorage.setItem(STORAGE_KEY_COMPANY_ID, selectedId);
      localStorage.setItem(STORAGE_KEY_COMPANY_NM, selectedName);
      if (bcUrl) localStorage.setItem(STORAGE_KEY_BC_URL, bcUrl);
      // Cleared up front, not left holding the previous company's values while the fetches
      // below are in flight -- an unrelated selection-refresh racing them (e.g. Outlook
      // re-firing ItemChanged) would otherwise search using a department value that belongs
      // to the wrong company. The "no department" banner flashing on every switch because of
      // that same window is a separate, UI-only concern -- handled by isDepartmentLoadPending
      // (see updateDepartmentStatusBanner in orders.ts), not by leaving storage stale here.
      localStorage.removeItem(STORAGE_KEY_COMPANY_ACRONYM);
      localStorage.removeItem(STORAGE_KEY_DEPARTMENT_SET_ENTRY_ID_FLT);
      void (async () => {
        if (selectedId && bcUrl) {
          await Promise.all([
            loadAndStoreCompanyAcronym(bcUrl, selectedId),
            loadAndStoreDepartmentSetEntryId(bcUrl, selectedId),
          ]);
        }
        if (onCompanyChanged) await onCompanyChanged();
      })();
    });
  }
}

/**
 * Brings stored bcUrl (and the acronym/departmentSetEntryIdFlt derived from it) back in
 * line with what the currently selected company actually resolves to, per the freshest
 * company fetch -- see the call site in populateCompanySelect for why this can drift
 * silently otherwise. A no-op when storage already agrees (the overwhelmingly common case).
 */
function resyncSelectedCompanyStorage(company: AggregatedCompany): void {
  if (!company.bcUrl) return;
  if ((localStorage.getItem(STORAGE_KEY_BC_URL) ?? "") === company.bcUrl) return;

  localStorage.setItem(STORAGE_KEY_BC_URL, company.bcUrl);
  // Cleared up front -- see the matching comment in populateCompanySelect's change handler.
  localStorage.removeItem(STORAGE_KEY_COMPANY_ACRONYM);
  localStorage.removeItem(STORAGE_KEY_DEPARTMENT_SET_ENTRY_ID_FLT);
  void loadAndStoreCompanyAcronym(company.bcUrl, company.id);
  void loadAndStoreDepartmentSetEntryId(company.bcUrl, company.id);
}
